import React, { useState } from 'react';
import { DeferralReason } from '@sandtech-hub-monorepo/ehub-client';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReactComponent as PauseIcon } from '../../../assets/Icons/pause-icon.svg';

import {
  Button,
  Form,
  FormField,
  FormLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@sandtech-hub-monorepo/ui-kit';
import { capitalize } from '@nx/devkit/src/utils/string-utils';
import { PauseProgramConfirmation } from './PauseProgramConfirmation';
import { ReasonsToEnum, UpdateCourseReasons } from './DeferCourse';
import { ProgramEnrollment } from './SelectProgram';

export const pauseFormSchema = z.object({
  reason: z.enum(UpdateCourseReasons),
  description: z.string(),
});

export const PauseProgram = ({
  onCancel,
  alreadyDeferred,
  programEnrollment,
}: {
  onCancel: () => void;
  alreadyDeferred: (string | undefined)[];
  programEnrollment: ProgramEnrollment;
}) => {
  const [selectedState, setSelectedState] = useState<'initial' | 'confirm'>(
    'initial'
  );

  const [confirmProps, setConfirmProps] = useState({
    description: '',
    reason: DeferralReason.Other,
  });

  const form = useForm<z.infer<typeof pauseFormSchema>>({
    resolver: zodResolver(pauseFormSchema),
    defaultValues: {
      description: '',
      reason: '',
    },
  });

  const onCancelClick = () => {
    form.reset();
    onCancel();
  };

  return (
    <>
      {selectedState === 'initial' && (
        <div className="max-w-[620px]">
          <div className="px-0 sm:px-16">
            <div className="flex flex-col items-center text-center gap-2">
              <div
                className={`p-6 bg-primary/20 rounded-full text-primary mx-auto mb-2`}
              >
                <PauseIcon height="48" width="48" />
              </div>
              <h2 className="text-primary sm:text-2xl text-xl">
                You are opting to:
              </h2>
              <h2 className={'text-primary font-semibold sm:text-2xl text-xl'}>
                Pause your program
              </h2>
              <div className="bg-primary/20 p-3 rounded-sm mt-8 text-secondary/70">
                <div>
                  <b>Please note: </b>
                </div>
                You will be enrolled in the next available class and will resume
                the program, but all current course content will need to be
                restarted.
              </div>
            </div>
            <div className="border-t-primary/20 my-8 pt-8 border-t">
              <h3 className="font-semibold text-lg">
                Why do you want to pause your program?
              </h3>
              <div className="opacity-70 font-light text-sm">
                We use this information for data purposes only and to improve
                the timing on our courses.
              </div>
              <Form {...form}>
                <FormField
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <Select
                      {...field}
                      onValueChange={(v) => {
                        field.onChange(v);
                        form.setValue('description', '');
                      }}
                      defaultValue="Select"
                    >
                      <SelectTrigger className="my-4">
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {UpdateCourseReasons.map((reason) => (
                          <SelectItem value={reason} key={reason}>
                            {capitalize(
                              reason.split('_').join(' ').toLowerCase()
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {form.watch('reason') === 'Other' && (
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <>
                        <FormLabel>Description</FormLabel>
                        <Textarea
                          {...field}
                          placeholder="Enter a description..."
                        />
                      </>
                    )}
                  />
                )}
              </Form>
            </div>
            <div className="flex gap-6 justify-center">
              <Button
                disabled={
                  !form.watch('reason') ||
                  (form.watch('reason') === DeferralReason.Other &&
                    !form.watch('description'))
                }
                onClick={() => {
                  setConfirmProps({
                    description: form.getValues('description'),
                    reason: ReasonsToEnum[form.getValues('reason')!],
                  });
                  setSelectedState('confirm');
                }}
              >
                Pause
              </Button>
              <Button variant="outline" onClick={onCancelClick}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {selectedState === 'confirm' && (
        <PauseProgramConfirmation
          {...confirmProps}
          programEnrollment={programEnrollment}
          afterConfirm={onCancelClick}
          onCancel={onCancelClick}
          alreadyDeferred={alreadyDeferred}
        />
      )}
    </>
  );
};
